import { Controller } from "@hotwired/stimulus";

// Infinite scroll / lazy-load controller for Pagy-backed pages.
//
// Markup contract:
// - data-controller="infinite-scroll" on a wrapper element
// - data-infinite-scroll-target="items" on the container to append into
// - data-infinite-scroll-target="next" on a hidden <a href="...page=N">
// - data-infinite-scroll-target="sentinel" on a small div after content
// - optional: data-infinite-scroll-target="pagination" wrapper (will be hidden when JS is active)
export default class extends Controller {
  static targets = ["items", "next", "sentinel", "pagination"];

  connect() {
    this.loading = false;

    // Hide standard pagination when JS is enabled; keep it for no-JS users.
    if (this.hasPaginationTarget) this.paginationTarget.classList.add("d-none");

    if (!this.hasSentinelTarget) return;
    if (!this.hasNextTarget || !this.nextTarget?.href) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) this.loadNext();
      },
      {
        root: null,
        rootMargin: "700px 0px",
        threshold: 0.01,
      }
    );

    this.observer.observe(this.sentinelTarget);
  }

  disconnect() {
    try {
      this.observer?.disconnect();
    } catch (_) {}
  }

  async loadNext() {
    if (this.loading) return;
    if (!this.hasItemsTarget) return;
    if (!this.hasNextTarget || !this.nextTarget?.href) return;

    this.loading = true;

    const nextUrl = this.nextTarget.href;

    const prevHTML = this.sentinelTarget.innerHTML;
    this.sentinelTarget.classList.add("is-loading");
    this.sentinelTarget.innerHTML = this.loadingHTML();

    try {
      const resp = await fetch(nextUrl, {
        method: "GET",
        credentials: "same-origin",
        headers: { Accept: "text/html" },
      });
      if (!resp.ok) throw new Error(`Failed to load next page (${resp.status})`);

      const html = await resp.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      const newItems = doc.querySelector('[data-infinite-scroll-target="items"]');
      if (!newItems) {
        this.stop();
        return;
      }

      // Append children into existing items container
      const fragment = document.createDocumentFragment();
      Array.from(newItems.children).forEach((child) => fragment.appendChild(child));
      this.itemsTarget.appendChild(fragment);

      // Update next link from the newly fetched page
      const newNext = doc.querySelector('[data-infinite-scroll-target="next"]');
      const newHref = newNext?.getAttribute("href");
      if (newHref) {
        this.nextTarget.setAttribute("href", newHref);
      } else {
        this.stop();
      }

      // Refresh AOS animations (if used on cards)
      try {
        const module = await import("../vendors/aos");
        const AOS = module.default || module;
        if (AOS?.refresh) AOS.refresh();
      } catch (_) {}
    } catch (e) {
      console.error(e);
      // If something goes wrong, show old pagination so user can still navigate.
      if (this.hasPaginationTarget) this.paginationTarget.classList.remove("d-none");
      this.stop();
    } finally {
      this.sentinelTarget.classList.remove("is-loading");
      this.sentinelTarget.innerHTML = prevHTML;
      this.loading = false;
    }
  }

  loadingHTML() {
    // Tables (dashboard) — compact shimmer rows.
    if (this.itemsTarget?.tagName === "TBODY") {
      return (
        '<div class="infinite-scroll-skeleton" style="grid-template-columns:1fr;margin:0 auto;">' +
        '<div class="infinite-scroll-skeleton__row" style="height:16px;"></div>' +
        '<div class="infinite-scroll-skeleton__row" style="height:16px;"></div>' +
        '<div class="infinite-scroll-skeleton__row" style="height:16px;"></div>' +
        '</div>'
      );
    }

    // Cards/grids.
    return (
      '<div class="infinite-scroll-skeleton" style="grid-template-columns:1fr;margin:0 auto;">' +
      '<div class="infinite-scroll-skeleton__card" style="height:110px;"></div>' +
      '</div>'
    );
  }

  stop() {
    try {
      if (this.observer && this.hasSentinelTarget) this.observer.unobserve(this.sentinelTarget);
      this.observer?.disconnect();
    } catch (_) {}

    // Remove sentinel to stop further loads
    try {
      this.sentinelTarget?.remove();
    } catch (_) {}

    // Remove next href so we don't try again
    try {
      this.nextTarget?.removeAttribute("href");
    } catch (_) {}
  }
}
